/**
 * The arithmetic behind the map viewer.
 *
 * Kept out of the component because it is the part that can actually be wrong. Pinching
 * two fingers on a phone is not reproducible in a test, but «zooming on a point leaves
 * that point where it was» is, and that is the property the whole thing rests on: a
 * viewer that drifts under your fingers is worse than a picture you cannot zoom.
 *
 * One coordinate convention throughout. The *frame* is the box on screen. The map is
 * laid out inside it at its `fitSize` — as large as it goes while whole — and then
 * transformed as `translate(x, y) scale(scale)` about the frame's centre. So `FIT` is
 * the whole map, centred, and every other view is a departure from it.
 */

export interface Size {
  width: number
  height: number
}

export interface Point {
  x: number
  y: number
}

export interface View {
  /** 1 is the whole map in the frame. Nothing zooms out past it — see `MIN_SCALE`. */
  scale: number
  /** Offset in frame pixels from the centred fit position. */
  x: number
  y: number
}

/** The whole map, centred: what the page opens on and what «tilbakestill» returns to. */
export const FIT: View = { scale: 1, x: 0, y: 0 }

/*
 * Zooming out past the fit would letterbox the map inside a frame it already fits, which
 * reads as the page having shrunk rather than the map. 6× is where the 3200px asset stops
 * adding detail on a phone and starts showing its own pixels.
 */
export const MIN_SCALE = 1
export const MAX_SCALE = 6

export function clampScale(scale: number): number {
  // Only a NaN has no side to fall to. An infinity is an ordinary out-of-range value
  // and clamps to the end it ran off, which is what a fast pinch on a bad frame rate
  // produces before it produces anything sensible.
  if (Number.isNaN(scale)) return MIN_SCALE
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))
}

/**
 * The map at its largest while still whole — the CSS `contain` rule, in numbers.
 *
 * The component lets CSS do the actual layout (`max-width`/`max-height` on the image),
 * so this has to agree with it exactly; both are the same «scale by the smaller ratio»
 * rule, which is why the clamping below can trust it.
 *
 * A frame of zero is the state before first layout, not an error: returning zeroes lets
 * the caller clamp to a centred `FIT` rather than divide by it.
 */
export function fitSize(image: Size, frame: Size): Size {
  if (image.width <= 0 || image.height <= 0) return { width: 0, height: 0 }
  if (frame.width <= 0 || frame.height <= 0) return { width: 0, height: 0 }
  const ratio = Math.min(frame.width / image.width, frame.height / image.height)
  return { width: image.width * ratio, height: image.height * ratio }
}

/**
 * Keeps the frame inside the map.
 *
 * The edge of the artwork is the edge of the world: you can reach any part of it, and
 * you cannot drag it off into empty space and lose it. On an axis where the scaled map
 * is no larger than the frame there is nothing to pan, so the offset is pinned to zero
 * and the map stays centred — which is what makes a fitted map sit still under a
 * one-finger drag instead of sliding around inside its own frame.
 */
export function clampView(view: View, fit: Size, frame: Size): View {
  const scale = clampScale(view.scale)
  const slackX = Math.max(0, (fit.width * scale - frame.width) / 2)
  const slackY = Math.max(0, (fit.height * scale - frame.height) / 2)
  return {
    scale,
    x: clamp(view.x, -slackX, slackX),
    y: clamp(view.y, -slackY, slackY),
  }
}

/**
 * Zooms by `factor` about `point`, which is measured from the frame's top-left.
 *
 * The point under the fingers — or the cursor, or the double tap — stays under them.
 * That is the whole contract, and the reason this is worth doing in numbers rather than
 * by eye: zoom that merely scales about the centre walks the thing you were looking at
 * off the screen after two pinches.
 *
 * Clamping the scale first and deriving the offset from the clamped value is deliberate.
 * Zooming past the ceiling then has no effect at all, rather than pinning the scale and
 * still shifting the map under a gesture that was refused.
 */
export function zoomAt(view: View, factor: number, point: Point, fit: Size, frame: Size): View {
  const scale = clampScale(view.scale * factor)
  // The point, and where it currently lands on the untransformed map.
  const px = point.x - frame.width / 2
  const py = point.y - frame.height / 2
  const mapX = (px - view.x) / view.scale
  const mapY = (py - view.y) / view.scale
  // Put that same spot of the map back under that same spot of the frame.
  return clampView({ scale, x: px - mapX * scale, y: py - mapY * scale }, fit, frame)
}

/** Drags the map by a frame-pixel delta, stopping at its edges. */
export function panBy(view: View, dx: number, dy: number, fit: Size, frame: Size): View {
  return clampView({ ...view, x: view.x + dx, y: view.y + dy }, fit, frame)
}

/** The gap between two touches, for reading a pinch as a factor. */
export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

/** The point a pinch is about: halfway between the two fingers. */
export function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return 0
  const clamped = Math.min(max, Math.max(min, value))
  // Clamping a negative offset against a zero bound yields -0, which is a different
  // value to every equality check that matters — including React's own bail-out on an
  // unchanged state. Two views that describe the same picture must compare equal.
  return clamped === 0 ? 0 : clamped
}
